import { useState } from 'react';
import { useUserPreferencesStore, ThemeMode, FontFamily, FontSize, FONT_FAMILIES, FONT_SIZES } from '../store';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const { theme, fontFamily, fontSize, setTheme, setFontFamily, setFontSize } = useUserPreferencesStore();
  
  if (!isOpen) return null;

  const fontOptions: { value: FontFamily; label: string }[] = [
    { value: 'system', label: 'System Default' },
    { value: 'inter', label: 'Inter' },
    { value: 'roboto', label: 'Roboto' },
    { value: 'georgia', label: 'Georgia (Serif)' },
    { value: 'merriweather', label: 'Merriweather (Serif)' },
  ];

  const sizeOptions: { value: FontSize; label: string; preview: string }[] = [
    { value: 'small', label: 'Small', preview: '12px' },
    { value: 'medium', label: 'Medium', preview: '14px' },
    { value: 'large', label: 'Large', preview: '16px' },
    { value: 'xlarge', label: 'Extra Large', preview: '18px' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-dark-800 rounded-xl shadow-2xl w-full max-w-md mx-4 border border-dark-600">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-dark-50">Settings</h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-dark-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-6">
          {/* Theme Mode */}
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-3">Theme</label>
            <div className="flex gap-3">
              <button
                onClick={() => setTheme('system')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  theme === 'system'
                    ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                    : 'border-dark-500 bg-dark-700 text-dark-300 hover:border-dark-400'
                }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17h4.5m-6 3h7.5M3 6.75A2.25 2.25 0 015.25 4.5h13.5A2.25 2.25 0 0121 6.75v7.5A2.25 2.25 0 0118.75 16.5H5.25A2.25 2.25 0 013 14.25v-7.5z" />
                </svg>
                System
              </button>
              <button
                onClick={() => setTheme('light')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  theme === 'light'
                    ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                    : 'border-dark-500 bg-dark-700 text-dark-300 hover:border-dark-400'
                }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Light
              </button>
              <button
                onClick={() => setTheme('dark')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                  theme === 'dark'
                    ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                    : 'border-dark-500 bg-dark-700 text-dark-300 hover:border-dark-400'
                }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
                Dark
              </button>
            </div>
          </div>

          {/* Font Family */}
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-3">Text Font</label>
            <select
              value={fontFamily}
              onChange={(e) => setFontFamily(e.target.value as FontFamily)}
              className="w-full px-4 py-3 rounded-lg bg-dark-700 border border-dark-500 text-dark-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {fontOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {/* Font Preview */}
            <div 
              className="mt-3 p-3 rounded-lg bg-dark-700 text-dark-200 text-sm"
              style={{ fontFamily: FONT_FAMILIES[fontFamily] }}
            >
              The quick brown fox jumps over the lazy dog.
            </div>
          </div>

          {/* Font Size */}
          <div>
            <label className="block text-sm font-medium text-dark-200 mb-3">Text Size</label>
            <div className="grid grid-cols-4 gap-2">
              {sizeOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFontSize(opt.value)}
                  className={`px-3 py-2 rounded-lg border-2 transition-all text-center ${
                    fontSize === opt.value
                      ? 'border-primary-500 bg-primary-500/10 text-primary-400'
                      : 'border-dark-500 bg-dark-700 text-dark-300 hover:border-dark-400'
                  }`}
                >
                  <div className="text-xs font-medium">{opt.label}</div>
                  <div className="text-[10px] text-dark-400">{opt.preview}</div>
                </button>
              ))}
            </div>
            {/* Size Preview */}
            <div 
              className="mt-3 p-3 rounded-lg bg-dark-700 text-dark-200"
              style={{ 
                fontFamily: FONT_FAMILIES[fontFamily],
                fontSize: FONT_SIZES[fontSize]
              }}
            >
              This is how your narrative text will appear.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-dark-600 flex justify-end">
          <button
            onClick={onClose}
            className="btn-primary"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}


